import { QuestionCircleOutlined } from '@ant-design/icons';
import { useLazyQuery, useQuery } from '@apollo/client';
import {
  ChannelIdeaEvaluationResult,
  Criteria,
  GET_BUSINESS_AREA_TAGS,
  GET_INTRAPRENEUR_CHALLENGES_BASIC,
  GET_TOP_IDEAS_BY_HIGHEST_SCORE,
  Idea,
  PocBusinessArea,
} from '@frontend/data-access';
import { UserAvatar } from '@frontend/shared/ui';
import {
  AutoComplete,
  AutoCompleteProps,
  Avatar,
  Input,
  Select,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import { ColumnType } from 'antd/lib/table';
import dayjs from 'dayjs';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useMemo, useState } from 'react';

const DEFAULT_LIMIT = 9;

const { Text } = Typography;

function TopIdeasByExpertStats({ className }: { className?: string }) {
  const { t } = useTranslation('admin');
  const { t: tcommon } = useTranslation('common');

  const [challengeId, setChallengeId] = useState<string | number | null>(null);
  const [options, setOptions] = useState<AutoCompleteProps['options']>([]);
  const [businessAreas, setBusinessAreas] = useState<string[]>([]);
  const [selectedChallengeName, setSelectedChallengeName] =
    useState<string>('');

  const [getChallenge] = useLazyQuery<{
    internalChallenges: { id: string; title: string }[];
  }>(GET_INTRAPRENEUR_CHALLENGES_BASIC, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      setOptions(
        data.internalChallenges.map((challenge) => ({
          value: challenge.id,
          label: challenge.title,
        }))
      );
    },
  });

  const [getTopIdeas, { data, loading }] = useLazyQuery<{
    dashboardIdeasByAverageScore: Idea[];
  }>(GET_TOP_IDEAS_BY_HIGHEST_SCORE, {
    fetchPolicy: 'no-cache',
  });

  const { data: businessArea } = useQuery<{
    pocBusinessAreaTags: PocBusinessArea[];
  }>(GET_BUSINESS_AREA_TAGS);

  const businessAreaOptions = useMemo(
    () =>
      businessArea?.pocBusinessAreaTags.map((b) => ({
        label: b.parentName ? `${b.parentName} - ${b.name}` : b.name,
        value: b.name,
      })),
    [businessArea]
  );

  useEffect(() => {
    getTopIdeas({
      variables: {
        challengeId: challengeId,
        businessArea: businessAreas,
        limit: 10,
      },
    });
  }, [businessAreas, challengeId, getTopIdeas]);

  const filterCriteriaResult = (
    data: ChannelIdeaEvaluationResult[]
  ): ChannelIdeaEvaluationResult[] => {
    return data.filter((r) => r?.status === 'DONE');
  };

  const results = useMemo(() => {
    return data?.dashboardIdeasByAverageScore.map((idea) => {
      return filterCriteriaResult(idea.channelCriteriaResults);
    });
  }, [data]);

  const criteria = useMemo(() => {
    return (
      data?.dashboardIdeasByAverageScore.map((idea) => {
        return idea.channelCriteria;
      }) || []
    );
  }, [data]);

  const IdeaAvatar = ({ data }: { data?: Idea }) => {
    const covertName = (name: string) => {
      const splittedStr = name.split(/(\s+)/);
      let str = '';
      splittedStr.forEach((s) => {
        if (s.trim() !== '') {
          str = str.concat(s.trim().charAt(0).toUpperCase());
        }
      });
      return str;
    };

    return (
      <Link href={'/idea-management/' + data?.id}>
        <div className="flex items-center gap-2 w-48">
          <Avatar
            shape="square"
            src={data?.coverPhotoUrl?.imageUrl || data?.coverPhoto?.url}
            className="cursor-pointer w-8 h-8"
          >
            <span>{data?.name && covertName(data.name)}</span>
          </Avatar>
          <Text className="flex-1 line-clamp-2">{data?.name}</Text>
        </div>
      </Link>
    );
  };

  const calculateAverageScoreIdeaChannel = (
    data: ChannelIdeaEvaluationResult[]
  ) => {
    const average =
      data?.reduce((prev, curr) => prev + curr?.score, 0) / data?.length;
    return isNaN(average) ? undefined : average.toFixed(1);
  };

  const CriteriaProgress = ({
    data,
    result,
  }: {
    data: Criteria;
    result: ChannelIdeaEvaluationResult[];
  }) => {
    const finalResult = result.filter(
      (r) => r?.criteria?.id === data?.id && r?.status === 'DONE'
    );
    const percent =
      (finalResult.reduce((prev, result) => prev + result?.score, 0) *
        data?.scale) /
      finalResult.length;

    return (
      <div className="flex justify-end text-center space-y-2 font-medium">
        {percent ? (percent / 10).toFixed(1) : '-'}
      </div>
    );
  };

  const columns: ColumnType<Idea>[] = [
    {
      dataIndex: 'index',
      key: 'index',
      title: t('no'),
      render: (_, __, index) => <div>{index + 1}.</div>,
    },
    {
      dataIndex: 'idea',
      key: 'idea',
      title: t('idea_name'),
      render: (_, record) => <IdeaAvatar data={record} />,
    },
    {
      dataIndex: 'createdBy',
      key: 'createdBy',
      title: t('created_by'),
      render: (_, record) => (
        <div className="flex space-x-2 items-center">
          <UserAvatar size={40} user={record.createdBy} showBadge />
          <div className="max-w-32 truncate">{record.createdBy?.fullName}</div>
        </div>
      ),
    },
    {
      dataIndex: 'averageScore',
      key: 'averageScore',
      title: (
        <div className="align-middle">
          {t('avg_score')}{' '}
          <Tooltip title={t('avg_score_tooltip')}>
            <QuestionCircleOutlined className="cursor-pointer" />
          </Tooltip>
        </div>
      ),
      width: 150,
      sorter: (a, b) => {
        return (
          parseFloat(
            calculateAverageScoreIdeaChannel(
              filterCriteriaResult(a.channelCriteriaResults)
            )
          ) -
          parseFloat(
            calculateAverageScoreIdeaChannel(
              filterCriteriaResult(b.channelCriteriaResults)
            )
          )
        );
      },
      defaultSortOrder: 'descend',
      sortDirections: ['descend', 'ascend'],
      render: (_, record, index: number) => {
        return (
          <div>
            {calculateAverageScoreIdeaChannel(
              filterCriteriaResult(record.channelCriteriaResults)
            ) || '-'}
          </div>
        );
      },
    },
    {
      dataIndex: 'desirability',
      key: 'desirability',
      title: (
        <div className="align-middle">
          D{' '}
          <Tooltip title={t('desirability_tooltip')}>
            <QuestionCircleOutlined className="cursor-pointer" />
          </Tooltip>
        </div>
      ),
      width: 70,
      render: (_, __, index: number) => {
        const value = criteria[index].find(
          (r) => r?.name.trim().toLowerCase() === 'desirability'
        );
        return (
          <Tooltip title={t('desirability')}>
            <CriteriaProgress data={value} result={results[index]} />
          </Tooltip>
        );
      },
    },
    {
      dataIndex: 'viability',
      key: 'viability',
      title: (
        <div className="align-middle">
          V{' '}
          <Tooltip title={t('viability_tooltip')}>
            <QuestionCircleOutlined className="cursor-pointer" />
          </Tooltip>
        </div>
      ),
      width: 70,
      render: (_, __, index: number) => {
        const value = criteria[index].find(
          (r) => r?.name.trim().toLowerCase() === 'viability'
        );
        return (
          <Tooltip title={t('viability')}>
            <CriteriaProgress data={value} result={results[index]} />
          </Tooltip>
        );
      },
    },
    {
      dataIndex: 'feasibility',
      key: 'feasibility',
      title: (
        <div className="align-middle">
          F{' '}
          <Tooltip title={t('feasibility_tooltip')}>
            <QuestionCircleOutlined className="cursor-pointer" />
          </Tooltip>
        </div>
      ),
      width: 70,
      render: (_, __, index: number) => {
        const value = criteria[index].find(
          (r) => r?.name.trim().toLowerCase() === 'feasibility'
        );
        return (
          <Tooltip title={t('feasibility')}>
            <CriteriaProgress data={value} result={results[index]} />
          </Tooltip>
        );
      },
    },
    {
      dataIndex: 'likes',
      key: 'likes',
      title: t('upvotes'),
      sorter: (a, b) => a.likes - b.likes,
      render: (value: number) => (
        <div>
          {value} {t('upvotes')}
        </div>
      ),
    },
    {
      dataIndex: 'contributeCount',
      key: 'contributeCount',
      title: t('contributions'),
      sorter: (a, b) => a.contributeCount - b.contributeCount,
      render: (value: number) => (
        <div>{t('count_contributions', { count: value })}</div>
      ),
    },
    {
      dataIndex: 'createdAt',
      key: 'createdAt',
      title: t('submitted_date'),
      render: (value: string) => (
        <div>{dayjs(value).format('DD MMM YYYY, HH:mm')}</div>
      ),
    },
    {
      dataIndex: ['innovationSpaceTag', 'name'],
      key: 'innovationSpaceTag',
      title: t('innovation_space'),
      width: 200,
      render: (value) =>
        value && (
          <div className="rounded-md py-1 px-3 bg-gray-200 text-gray-900 w-fit line-clamp-2 max-w-32 text-wrap">
            <span className="font-normal text-xs">{value}</span>
          </div>
        ),
    },
  ];

  const handleBusinessAreaChange = (value: string[]) => {
    setBusinessAreas(value);
  };

  const onSearch = (query: string) => {
    getChallenge({
      variables: {
        page: 0,
        limit: DEFAULT_LIMIT,
        filterBy: {
          states: ['ACTIVE', 'DRAFT', 'ENDED', 'UPCOMING', 'ARCHIVED'],
          challengeName: query,
        },
        sort: 'CREATION_DATE',
      },
    });
  };

  const onChange = (value: string) => {
    if (!value) {
      setChallengeId(null);
      setSelectedChallengeName('');
    } else {
      setSelectedChallengeName(value);
      // Only clear the challenge ID if we're typing something new (not matching a selection)
      const matchingChallenge = options.find(
        (option) => option.label === value
      );
      if (!matchingChallenge) {
        setChallengeId(null);
      }
    }
  };

  const onSelect = (challengeId: string) => {
    setChallengeId(challengeId || null);
    // Find the challenge name from options based on the selected ID
    const selectedOption = options.find(
      (option) => option.value === challengeId
    );
    if (selectedOption) {
      setSelectedChallengeName(selectedOption.label as string);
    }
  };

  return (
    <div className={className}>
      <div className="flex justify-between font-medium mb-6 space-x-4 items-start">
        <span>{t('top_10_ideas')}</span>
        <div className="flex flex-wrap gap-2">
          <div>
            <div className="flex gap-2 items-center">
              <div>{t('business_area')}</div>
            </div>
            <Select
              onChange={handleBusinessAreaChange}
              options={businessAreaOptions}
              value={businessAreas}
              maxTagCount="responsive"
              placeholder={t('search_idea_by_business_area')}
              allowClear
              mode="multiple"
              className="!w-[250px]"
              optionFilterProp="label"
            />
          </div>
          <div>
            <div className="flex gap-2 items-center">
              <div>{tcommon('select_challenge')}</div>
              <Tooltip title={t('search_idea_by_challenge_tooltip')}>
                <QuestionCircleOutlined className="cursor-pointer" />
              </Tooltip>
            </div>
            <AutoComplete
              className="!w-[250px]"
              popupClassName="certain-category-search-dropdown"
              style={{ width: '100%' }}
              options={options}
              onChange={onChange}
              onSelect={onSelect}
              onSearch={onSearch}
              value={selectedChallengeName}
            >
              <Input.Search
                placeholder={t('search_by_challenge_name')}
                allowClear
              />
            </AutoComplete>
          </div>
        </div>
      </div>
      <Table
        className="overflow-auto"
        columns={columns}
        dataSource={data?.dashboardIdeasByAverageScore || []}
        pagination={false}
        bordered
        loading={loading}
        rowKey="id"
      />
    </div>
  );
}

export default TopIdeasByExpertStats;
